/**
 * C5 · Collector Analytics drilldown drawer.
 *
 * Admin-only, read-only side drawer that lists the collectors behind
 * a Collector-Analytics KPI. Reuses the C5 permanent endpoint at
 * GET /api/admin/collector-analytics/drilldown/{metric_key}.
 *
 * Not a redesign of the analytics dashboard. The drawer opens as an
 * overlay on top of the dashboard so the owner can review a metric
 * and immediately return without losing dashboard state.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  X, Search, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle,
  ExternalLink,
} from "lucide-react";

// Which metric-specific numeric column to render on the row.
const METRIC_COUNT_FIELD = {
  album_owners: { field: "album_count", label: (n) => `${n} album${n === 1 ? "" : "s"}` },
  tracking_users: { field: "tracked_album_count", label: (n) => `${n} tracked album${n === 1 ? "" : "s"}` },
  missing_users: { field: "missing_count", label: (n) => `${n} missing sticker${n === 1 ? "" : "s"}` },
  duplicate_users: { field: "duplicate_quantity", label: (n) => `${n} duplicate${n === 1 ? "" : "s"}` },
  completed_album_collectors: { field: "completed_album_count", label: (n) => `${n} completed album${n === 1 ? "" : "s"}` },
  certificate_holders: { field: "approved_certificate_count", label: (n) => `${n} certificate${n === 1 ? "" : "s"}` },
};

function useDebounce(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

export default function AnalyticsDrilldownDrawer({ open, metricKey, onClose }) {
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [sort, setSort] = useState("display_name");
  const [order, setOrder] = useState("asc");
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 350);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Reset paging when metric changes.
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setQ("");
    setSort("display_name");
    setOrder("asc");
    setErr(null);
    setData(null);
  }, [metricKey, open]);

  const load = useCallback(async () => {
    if (!open || !metricKey) return;
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams({
        page: String(page), limit: String(limit),
        sort, order,
      });
      if (debouncedQ) params.set("q", debouncedQ);
      const r = await api.get(
        `/admin/collector-analytics/drilldown/${metricKey}?${params}`
      );
      setData(r.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load drilldown.");
    } finally {
      setLoading(false);
    }
  }, [open, metricKey, page, limit, sort, order, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  // Close on ESC key.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const countCfg = METRIC_COUNT_FIELD[metricKey];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="analytics-drilldown-overlay"
    >
      {/* Backdrop */}
      <button
        aria-label="Close drilldown"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
        data-testid="analytics-drilldown-backdrop"
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        className="relative bg-white border-l-2 border-[var(--ink)] w-full sm:w-[560px] max-w-full h-full overflow-y-auto shadow-[-6px_0_0_var(--ink)] flex flex-col"
        data-testid="analytics-drilldown-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
                Drilldown
              </div>
              <h2
                className="font-display text-xl"
                data-testid="analytics-drilldown-title"
              >
                {data?.label ||
                  (metricKey || "").replace(/_/g, " ")}
              </h2>
              <div
                className="text-xs text-[var(--smoke)] mt-0.5"
                data-testid="analytics-drilldown-total"
              >
                {loading && !data ? "Loading…" : `${(data?.total || 0).toLocaleString("en-US")} collector${(data?.total || 0) === 1 ? "" : "s"}`}
                {data?.data_quality?.orphaned_collector_ids > 0 && (
                  <span className="ml-2 text-yellow-800">
                    · {data.data_quality.orphaned_collector_ids} orphaned id
                    {data.data_quality.orphaned_collector_ids === 1 ? "" : "s"}
                    {" "}(collector row missing)
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full border-2 border-[var(--ink)] hover:bg-neutral-50"
              data-testid="analytics-drilldown-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Search + Sort */}
          <div className="mt-3 flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[160px]">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--smoke)]"
              />
              <input
                type="search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search name, ID, email"
                className="w-full border-2 border-[var(--ink)] rounded-full pl-8 pr-3 py-1.5 text-sm bg-white"
                data-testid="analytics-drilldown-search"
              />
            </div>
            <select
              value={`${sort}:${order}`}
              onChange={(e) => {
                const [s, o] = e.target.value.split(":");
                setSort(s); setOrder(o); setPage(1);
              }}
              className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-sm bg-white"
              data-testid="analytics-drilldown-sort"
            >
              <option value="display_name:asc">Name A → Z</option>
              <option value="display_name:desc">Name Z → A</option>
              <option value="created_at:desc">Newest first</option>
              <option value="created_at:asc">Oldest first</option>
              {countCfg && (
                <>
                  <option value="metric_count:desc">Highest count</option>
                  <option value="metric_count:asc">Lowest count</option>
                </>
              )}
            </select>
            <button
              type="button"
              onClick={load}
              className="p-2 rounded-full border-2 border-[var(--ink)] hover:bg-neutral-50 disabled:opacity-50"
              data-testid="analytics-drilldown-refresh"
              disabled={loading}
              aria-label="Refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 p-3 sm:p-4">
          {err && (
            <div
              className="rounded-xl border-2 border-red-600 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2"
              data-testid="analytics-drilldown-error"
            >
              <AlertTriangle size={16} className="mt-0.5" />
              <div className="flex-1">
                <div className="font-bold">Could not load drilldown</div>
                <div>{err}</div>
                <button
                  type="button"
                  onClick={load}
                  className="mt-2 rounded-full border-2 border-red-700 px-3 py-1 text-xs font-bold hover:bg-red-100"
                  data-testid="analytics-drilldown-retry"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {loading && !data && !err && (
            <div
              className="text-sm text-[var(--smoke)] italic py-8 text-center"
              data-testid="analytics-drilldown-loading"
            >
              Loading collectors…
            </div>
          )}

          {data && data.total === 0 && !loading && (
            <div
              className="text-sm text-[var(--smoke)] italic py-8 text-center"
              data-testid="analytics-drilldown-empty"
            >
              No collectors match this metric.
            </div>
          )}

          {data && data.rows && data.rows.length > 0 && (
            <ul
              className="divide-y divide-neutral-100"
              data-testid="analytics-drilldown-rows"
            >
              {data.rows.map((row) => {
                const cnt = countCfg ? row[countCfg.field] : null;
                return (
                  <li
                    key={row.collector_id}
                    className="py-2.5 flex items-start justify-between gap-3"
                    data-testid={`drilldown-row-${row.collector_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold truncate">
                        {row.display_name}
                      </div>
                      <div className="text-[10px] font-mono uppercase text-[var(--smoke)] tracking-wider mt-0.5">
                        {row.public_collector_id || "—"}
                        {row.masked_email && (
                          <span className="normal-case font-sans text-neutral-500">
                            {" "}· {row.masked_email}
                          </span>
                        )}
                      </div>
                      {row.profile_public === false && (
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                          Private profile
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs">
                      {countCfg && cnt != null && (
                        <div
                          className="font-bold text-[var(--ink)]"
                          data-testid={`drilldown-row-${row.collector_id}-metric`}
                        >
                          {countCfg.label(cnt)}
                        </div>
                      )}
                      {row.username && (
                        <Link
                          to={`/collectors/${row.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[var(--panini-red)] hover:underline mt-1"
                          data-testid={`drilldown-row-${row.collector_id}-view`}
                        >
                          View <ExternalLink size={11} />
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination footer */}
        {data && data.total_pages > 1 && (
          <div
            className="sticky bottom-0 bg-white border-t-2 border-[var(--ink)] p-3 flex items-center justify-between gap-2"
            data-testid="analytics-drilldown-pagination"
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || page <= 1}
              className="rounded-full border-2 border-[var(--ink)] px-3 py-1 text-xs font-bold flex items-center gap-1 disabled:opacity-40 hover:bg-neutral-50"
              data-testid="analytics-drilldown-prev"
            >
              <ChevronLeft size={12} /> Prev
            </button>
            <div className="text-xs text-[var(--smoke)]">
              Page <span className="font-bold text-[var(--ink)]">{data.page}</span> of {data.total_pages}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
              disabled={loading || page >= data.total_pages}
              className="rounded-full border-2 border-[var(--ink)] px-3 py-1 text-xs font-bold flex items-center gap-1 disabled:opacity-40 hover:bg-neutral-50"
              data-testid="analytics-drilldown-next"
            >
              Next <ChevronRight size={12} />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
